import React, { useEffect } from "react";

import {
  DescriptionOutlined,
  ExpandMore,
  OpenInNew,
  VideocamOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { mailTo } from "@/common/utils";

import TrainingVideo, { TrainingVideoProps } from "./TrainingVideo";

export interface TrainingSection {
  id: string;
  heading: string;
  video?: TrainingVideoProps;
  resources?: {
    title: string;
    href: string;
    previewUrl?: string;
  }[];
}

interface TrainingHubProps {
  title: string;
  sections: TrainingSection[];
  support: {
    prefix: string;
    suffix: string;
    emailSubject: string;
    emailBody: string;
  };
}

const TrainingHub: React.FC<TrainingHubProps> = ({
  title,
  sections,
  support,
}) => {
  const { t } = useTranslation("training");
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const expandedSectionId = sections.find(
    (section) => location.hash === `#${section.id}`,
  )?.id;
  const trainingRequestMailto = mailTo({
    email: "support@tryrubicon.com",
    subject: support.emailSubject,
    body: support.emailBody,
  });

  useEffect(() => {
    if (!expandedSectionId) return;

    document
      .getElementById(expandedSectionId)
      ?.scrollIntoView?.({ block: "start" });
  }, [expandedSectionId]);

  const setSection = (sectionId?: string) => {
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: sectionId ? `#${sectionId}` : "",
      },
      { replace: true, state: location.state },
    );
  };

  return (
    <Box
      sx={(theme) => ({
        p: theme.spacing(4, 2),
        margin: "0 auto",
        maxWidth: isDesktop ? theme.spacing(160) : "var(--page-max-width)",
        width: "100%",
      })}
    >
      <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
        {title}
      </Typography>

      <Box
        sx={(theme) => ({
          alignItems: "flex-start",
          display: isDesktop ? "flex" : "block",
          gap: isDesktop ? theme.spacing(4) : 0,
          mt: theme.spacing(4),
        })}
      >
        {isDesktop && (
          <Box
            component="nav"
            aria-label={title}
            sx={(theme) => ({
              flex: "0 0 auto",
              position: "sticky",
              top: theme.spacing(3),
              width: theme.spacing(28),
            })}
          >
            <Stack spacing={1}>
              {sections.map((section) => (
                <Link
                  key={section.id}
                  href={`${location.pathname}${location.search}#${section.id}`}
                  aria-current={
                    expandedSectionId === section.id ? "location" : undefined
                  }
                  onClick={(event) => {
                    event.preventDefault();
                    if (expandedSectionId === section.id) {
                      document
                        .getElementById(section.id)
                        ?.scrollIntoView?.({ block: "start" });
                    } else {
                      setSection(section.id);
                    }
                  }}
                  sx={{
                    fontWeight: expandedSectionId === section.id ? 600 : 400,
                    textAlign: "left",
                  }}
                  underline={
                    expandedSectionId === section.id ? "always" : "hover"
                  }
                >
                  {section.heading}
                </Link>
              ))}
            </Stack>
          </Box>
        )}

        <Stack spacing={2} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          {sections.map((section) => (
            <Accordion
              key={section.id}
              id={section.id}
              component="section"
              expanded={expandedSectionId === section.id}
              onChange={(_event, isExpanded) =>
                setSection(isExpanded ? section.id : undefined)
              }
              slotProps={{
                heading: { component: "h2" },
                transition: { unmountOnExit: true },
              }}
              sx={(theme) => ({ scrollMarginTop: theme.spacing(2) })}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`${section.id}-content`}
                id={`${section.id}-header`}
              >
                <Typography
                  variant="h5"
                  component="span"
                  sx={{ fontWeight: 600 }}
                >
                  {section.heading}
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Stack spacing={2}>
                  {section.video && (
                    <>
                      <Link
                        href={`https://www.loom.com/share/${section.video.loomId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t("opensInNewTab")}
                        sx={{
                          alignItems: "center",
                          alignSelf: "flex-start",
                          display: "inline-flex",
                          gap: 1,
                        }}
                      >
                        <VideocamOutlined fontSize="small" />
                        <span>
                          {section.video.title}
                          <OpenInNew
                            fontSize="inherit"
                            sx={{ ml: 0.5, verticalAlign: "middle" }}
                          />
                        </span>
                      </Link>
                      <TrainingVideo
                        key={section.video.loomId}
                        {...section.video}
                      />
                    </>
                  )}
                  {section.resources?.map((resource) => (
                    <Stack key={resource.href} spacing={2}>
                      <Link
                        href={resource.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t("opensInNewTab")}
                        sx={{
                          alignItems: "center",
                          alignSelf: "flex-start",
                          display: "inline-flex",
                          gap: 1,
                        }}
                      >
                        <DescriptionOutlined fontSize="small" />
                        <span>
                          {resource.title}
                          <OpenInNew
                            fontSize="inherit"
                            sx={{ ml: 0.5, verticalAlign: "middle" }}
                          />
                        </span>
                      </Link>
                      {resource.previewUrl && isDesktop && (
                        <Box
                          component="iframe"
                          src={resource.previewUrl}
                          title={t("documentPreviewTitle", {
                            title: resource.title,
                          })}
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                          sx={(theme) => ({
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            display: "block",
                            height: theme.spacing(80),
                            width: "100%",
                          })}
                        />
                      )}
                    </Stack>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Box>

      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          fontStyle: "italic",
          fontWeight: 600,
          mt: 5,
        }}
      >
        {support.prefix}{" "}
        <Link href={trainingRequestMailto}>support@tryrubicon.com</Link>{" "}
        {support.suffix}
      </Typography>
    </Box>
  );
};

export default TrainingHub;
